/**
 * Cart Validation Utility
 * Prevents adding mutually exclusive products to cart
 * Uses collection-based validation (primary) and product tags (fallback)
 * 
 * COLLECTION-BASED VALIDATION:
 * - Products from the same collection can be ordered together
 * - Products from different exclusive collections cannot be ordered together
 * 
 * CONFIGURATION:
 * - Exclusive collections are defined in this.exclusiveCollections array
 * - Collection IDs are passed via data-collections attribute on cart items (set in Liquid)
 */

class CartValidation {
  constructor() {
    // Collection-based configuration (primary method)
    // Products from the same collection can be together
    // Products from different collections cannot be ordered together
    this.exclusiveCollections = window.cartValidationConfig?.exclusiveCollections || [
      '500309328179',  // Collection ID 1
      '501320614195'   // Collection ID 2
    ];
    
    // Tag-based configuration (fallback/legacy)
    this.exclusiveTags = window.cartValidationConfig?.exclusiveTags || [
      'galette-des-rois'
    ];
    
    this.groupTags = window.cartValidationConfig?.groupTags || [
      'panettone-group'
    ];
    
    // Cache for product tags and collections
    this.productTagCache = new Map();
    this.productCollectionCache = new Map();
    
    // Hardcoded product conflict rules (fallback/legacy)
    this.productConflicts = {
      10021463195955: [9970710020403, 9970662572339],
      9970710020403: [10021463195955],
      9970662572339: [10021463195955]
    };
  }

  /**
   * Get current cart items
   * @returns {Promise<Array>} Array of cart items
   */
  async getCartItems() {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`);
      const cart = await response.json();
      return cart.items || [];
    } catch (error) {
      console.error('Error fetching cart:', error);
      return [];
    }
  }

  /**
   * Fetch product tags from Shopify API
   * @param {number} productId - Product ID
   * @returns {Promise<Array|null>} Array of tags or null
   */
  async fetchProductTags(productId) {
    if (this.productTagCache.has(productId)) {
      return this.productTagCache.get(productId);
    }

    try {
      const shopUrl = this.getShopUrl();
      const response = await fetch(`${shopUrl}/products/${productId}.js`);
      if (!response.ok) {
        return null;
      }
      
      const product = await response.json();
      const tags = product.tags || [];
      this.productTagCache.set(productId, tags);
      return tags;
    } catch (error) {
      console.error(`Error fetching product ${productId}:`, error);
      return null;
    }
  }

  /**
   * Get shop URL for API calls
   * @returns {string} Shop URL
   */
  getShopUrl() {
    if (window.shopUrl) return window.shopUrl;
    if (window.Shopify?.shop) return `https://${window.Shopify.shop}`;
    return window.location.origin;
  }

  /**
   * Fetch product collections from Shopify API (fallback method)
   * Primary method uses DOM data attributes set by Liquid
   * @param {number} productId - Product ID
   * @returns {Promise<Array<string>|null>} Array of collection IDs or null
   */
  async fetchProductCollections(productId) {
    if (this.productCollectionCache.has(productId)) {
      return this.productCollectionCache.get(productId);
    }

    try {
      const collectionIds = [];
      const shopUrl = this.getShopUrl();
      
      // Check each exclusive collection
      for (const collectionId of this.exclusiveCollections) {
        try {
          const productsResponse = await fetch(`${shopUrl}/collections/${collectionId}/products.js?limit=250`);
          if (productsResponse.ok) {
            const products = await productsResponse.json();
            const productInCollection = products.find(p => 
              p.id === productId || String(p.id) === String(productId)
            );
            if (productInCollection) {
              collectionIds.push(String(collectionId));
            }
          }
        } catch (e) {
          // Continue checking other collections
        }
      }
      
      this.productCollectionCache.set(productId, collectionIds);
      return collectionIds.length > 0 ? collectionIds : null;
    } catch (error) {
      console.error(`Error fetching product collections ${productId}:`, error);
      return null;
    }
  }

  /**
   * Get product collections from cart item
   * Checks DOM data attributes first (set by Liquid), then cart item object
   * @param {Object} cartItem - Cart item object
   * @returns {Array<string>} Array of collection IDs
   */
  getProductCollectionsFromCartItem(cartItem) {
    // Method 1: Check data attribute from DOM (most reliable)
    if (cartItem.product_id) {
      const selectors = [
        `[data-product-id="${cartItem.product_id}"]`,
        `tr[data-product-id="${cartItem.product_id}"]`,
        `.cart-item[data-product-id="${cartItem.product_id}"]`
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element) {
          const collectionsAttr = element.getAttribute('data-collections');
          if (collectionsAttr) {
            const collections = collectionsAttr.split(',')
              .map(id => String(id.trim()))
              .filter(id => id);
            if (collections.length > 0) {
              return collections;
            }
          }
        }
      }
    }
    
    // Method 2: Try to get collections from cart item object
    if (cartItem.product?.collections && Array.isArray(cartItem.product.collections)) {
      const collections = cartItem.product.collections
        .map(col => String(col.id || col))
        .filter(id => this.exclusiveCollections.includes(id));
      if (collections.length > 0) {
        return collections;
      }
    }
    
    return [];
  }

  /**
   * Get the exclusive collection ID that a product belongs to (if any)
   * @param {number} productId - Product ID
   * @param {Array<string>} collectionIds - Product collection IDs (optional)
   * @returns {Promise<string|null>} Exclusive collection ID or null
   */
  async getProductExclusiveCollection(productId, collectionIds = null) {
    if (!collectionIds || collectionIds.length === 0) {
      collectionIds = await this.fetchProductCollections(productId);
    }
    
    if (!collectionIds || !Array.isArray(collectionIds)) {
      return null;
    }

    // Check if product belongs to any of our exclusive collections
    for (const collectionId of this.exclusiveCollections) {
      if (collectionIds.includes(String(collectionId))) {
        return String(collectionId);
      }
    }

    return null;
  }

  /**
   * Get product tags from cart item
   * @param {Object} cartItem - Cart item object
   * @returns {Array} Array of product tags
   */
  getProductTagsFromCartItem(cartItem) {
    if (cartItem.product?.tags && Array.isArray(cartItem.product.tags)) {
      return cartItem.product.tags;
    }
    if (cartItem.tags && Array.isArray(cartItem.tags)) {
      return cartItem.tags;
    }
    return [];
  }

  /**
   * Get exclusive tag from product (if it has any exclusive tags)
   * @param {number} productId - Product ID
   * @param {Array} tags - Product tags (optional)
   * @returns {Promise<string|null>} Exclusive tag or null
   */
  async getProductExclusiveTag(productId, tags = null) {
    if (!tags) {
      tags = await this.fetchProductTags(productId);
    }
    
    if (!tags || !Array.isArray(tags)) {
      return null;
    }

    for (const tag of tags) {
      if (this.exclusiveTags.includes(tag)) {
        return tag;
      }
    }

    return null;
  }

  /**
   * Check hardcoded product conflicts (fallback method)
   * @param {number} productIdToAdd - Product ID being added
   * @param {Array} cartItems - Current cart items
   * @returns {Object|null} Conflict info or null
   */
  checkHardcodedConflicts(productIdToAdd, cartItems) {
    const conflictsWith = this.productConflicts[productIdToAdd];
    if (conflictsWith && conflictsWith.length > 0) {
      for (const cartItem of cartItems) {
        if (cartItem.product_id === productIdToAdd) continue;
        
        if (conflictsWith.includes(cartItem.product_id)) {
          const conflictProduct = cartItem.product?.title || cartItem.title || 'another product';
          return {
            isValid: false,
            conflictProduct: conflictProduct,
            conflictType: 'hardcoded',
            message: this.getErrorMessage(conflictProduct, productIdToAdd)
          };
        }
      }
    }
    
    // Check if any cart item conflicts with the product being added
    for (const cartItem of cartItems) {
      if (cartItem.product_id === productIdToAdd) continue;
      
      const cartItemConflicts = this.productConflicts[cartItem.product_id];
      if (cartItemConflicts && cartItemConflicts.includes(productIdToAdd)) {
        const conflictProduct = cartItem.product?.title || cartItem.title || 'another product';
        return {
          isValid: false,
          conflictProduct: conflictProduct,
          conflictType: 'hardcoded',
          message: this.getErrorMessage(conflictProduct, productIdToAdd)
        };
      }
    }
    
    return null;
  }

  /**
   * Check if adding a product would violate exclusivity rules
   * Priority: Collection-based → Tag-based → Hardcoded conflicts
   * @param {number} productIdToAdd - Product ID to add
   * @param {Array} currentCartItems - Current cart items (optional)
   * @returns {Promise<Object>} Validation result
   */
  async validateProductAddition(productIdToAdd, currentCartItems = null) {
    const cartItems = currentCartItems || await this.getCartItems();

    // PRIORITY 1: Collection-based conflicts
    const productCollections = await this.fetchProductCollections(productIdToAdd);
    const productExclusiveCollection = await this.getProductExclusiveCollection(productIdToAdd, productCollections);
    
    if (productExclusiveCollection) {
      for (const cartItem of cartItems) {
        if (cartItem.product_id === productIdToAdd) continue;

        const cartItemCollections = this.getProductCollectionsFromCartItem(cartItem);
        const cartItemExclusiveCollection = cartItemCollections.length > 0 
          ? await this.getProductExclusiveCollection(cartItem.product_id, cartItemCollections)
          : await this.getProductExclusiveCollection(cartItem.product_id);
        
        // Products from different exclusive collections conflict
        if (cartItemExclusiveCollection && cartItemExclusiveCollection !== productExclusiveCollection) {
          const conflictProduct = cartItem.product?.title || cartItem.title || 'another product';
          return {
            isValid: false,
            conflictProduct: conflictProduct,
            collection: productExclusiveCollection,
            conflictType: 'collection',
            message: this.getErrorMessage(conflictProduct, productIdToAdd)
          };
        }
      }
      return { isValid: true, conflictProduct: null, collection: null };
    }

    // PRIORITY 2: Tag-based conflicts
    const productTags = await this.fetchProductTags(productIdToAdd);
    const productExclusiveTag = await this.getProductExclusiveTag(productIdToAdd, productTags);
    
    if (productExclusiveTag) {
      for (const cartItem of cartItems) {
        if (cartItem.product_id === productIdToAdd) continue;

        const cartItemTags = this.getProductTagsFromCartItem(cartItem);
        const cartItemExclusiveTag = cartItemTags.length > 0 
          ? await this.getProductExclusiveTag(cartItem.product_id, cartItemTags)
          : await this.getProductExclusiveTag(cartItem.product_id);
        
        if (cartItemExclusiveTag && cartItemExclusiveTag === productExclusiveTag) {
          const conflictProduct = cartItem.product?.title || cartItem.title || 'another product';
          return {
            isValid: false,
            conflictProduct: conflictProduct,
            tag: productExclusiveTag,
            conflictType: 'tag',
            message: this.getErrorMessage(conflictProduct, productIdToAdd)
          };
        }
      }
      return { isValid: true, conflictProduct: null, tag: null };
    }

    // PRIORITY 3: Hardcoded conflicts
    const hardcodedConflict = this.checkHardcodedConflicts(productIdToAdd, cartItems);
    if (hardcodedConflict) {
      return hardcodedConflict;
    }

    return { isValid: true, conflictProduct: null, tag: null, collection: null };
  }

  /**
   * Get error message for conflict
   * @param {string} conflictProduct - Name of conflicting product
   * @param {number} productIdToAdd - Product ID being added
   * @returns {string} Error message
   */
  getErrorMessage(conflictProduct, productIdToAdd) {
    const conflictLower = conflictProduct.toLowerCase();
    const isGalettePanettoneConflict = 
      conflictLower.includes('galette') || 
      conflictLower.includes('rois') || 
      conflictLower.includes('panettone');
    
    if (isGalettePanettoneConflict) {
      const defaultMessage = `This product has a different availability period than "${conflictProduct}" in your cart. Galette des Rois is available from December 26, 2025 to January 31, 2026, while Classic Panettone is only available until December 25, 2025. Please complete your order for one product first, then place a separate order for the other.`;
      
      if (window.cartValidationStrings?.exclusiveProductErrorSpecific) {
        return window.cartValidationStrings.exclusiveProductErrorSpecific
          .replace('[conflict_product]', conflictProduct);
      }
      return defaultMessage;
    }
    
    const defaultMessage = `This product has a different availability period than "${conflictProduct}" in your cart. Please complete your order for one product first, then place a separate order for the other.`;
    
    if (window.cartValidationStrings?.exclusiveProductError) {
      return window.cartValidationStrings.exclusiveProductError
        .replace('[conflict_product]', conflictProduct);
    }
    
    return defaultMessage;
  }

  /**
   * Validate cart update (when changing quantities)
   * @param {number} variantId - Variant ID being updated
   * @param {number} newQuantity - New quantity
   * @param {Array} currentCartItems - Current cart items
   * @returns {Promise<Object>} Validation result
   */
  async validateCartUpdate(variantId, newQuantity, currentCartItems = null) {
    const cartItems = currentCartItems || await this.getCartItems();
    
    if (newQuantity === 0) {
      return { isValid: true, conflictProduct: null, group: null };
    }

    const itemBeingUpdated = cartItems.find(item => item.variant_id === variantId);
    if (!itemBeingUpdated) {
      return { isValid: true, conflictProduct: null, group: null };
    }

    return await this.validateProductAddition(itemBeingUpdated.product_id, cartItems);
  }

  /**
   * Clear product tag and collection cache
   */
  clearCache() {
    this.productTagCache.clear();
    this.productCollectionCache.clear();
  }

  /**
   * Clear cache for a specific product
   * @param {number} productId - Product ID
   */
  clearProductCache(productId) {
    this.productTagCache.delete(productId);
    this.productCollectionCache.delete(productId);
  }

  /**
   * Check current cart for conflicts
   * Priority: Collection-based → Tag-based → Hardcoded conflicts
   * @param {Array} cartItems - Current cart items (optional)
   * @returns {Promise<Array>} Array of conflict objects
   */
  async checkCartConflicts(cartItems = null) {
    const items = cartItems || await this.getCartItems();
    const conflicts = [];

    if (!items || items.length === 0) {
      return conflicts;
    }

    // Check each product against all others
    for (let i = 0; i < items.length; i++) {
      const item1 = items[i];
      
      for (let j = i + 1; j < items.length; j++) {
        const item2 = items[j];
        
        // PRIORITY 1: Collection-based conflicts
        const collections1 = this.getProductCollectionsFromCartItem(item1);
        const collections2 = this.getProductCollectionsFromCartItem(item2);
        
        const exclusiveCollection1 = collections1.length > 0 
          ? await this.getProductExclusiveCollection(item1.product_id, collections1)
          : await this.getProductExclusiveCollection(item1.product_id);
        const exclusiveCollection2 = collections2.length > 0
          ? await this.getProductExclusiveCollection(item2.product_id, collections2)
          : await this.getProductExclusiveCollection(item2.product_id);
        
        if (exclusiveCollection1 && exclusiveCollection2 && exclusiveCollection1 !== exclusiveCollection2) {
          conflicts.push({
            product1: {
              id: item1.product_id,
              title: item1.product?.title || item1.title || 'Product'
            },
            product2: {
              id: item2.product_id,
              title: item2.product?.title || item2.title || 'Product'
            },
            type: 'collection',
            collection1: exclusiveCollection1,
            collection2: exclusiveCollection2
          });
          continue;
        }
        
        // PRIORITY 2: Tag-based conflicts
        const tags1 = this.getProductTagsFromCartItem(item1);
        const tags2 = this.getProductTagsFromCartItem(item2);
        
        const exclusiveTag1 = tags1.length > 0 
          ? await this.getProductExclusiveTag(item1.product_id, tags1)
          : await this.getProductExclusiveTag(item1.product_id);
        const exclusiveTag2 = tags2.length > 0
          ? await this.getProductExclusiveTag(item2.product_id, tags2)
          : await this.getProductExclusiveTag(item2.product_id);
        
        if (exclusiveTag1 && exclusiveTag2 && exclusiveTag1 === exclusiveTag2) {
          conflicts.push({
            product1: {
              id: item1.product_id,
              title: item1.product?.title || item1.title || 'Product'
            },
            product2: {
              id: item2.product_id,
              title: item2.product?.title || item2.title || 'Product'
            },
            type: 'tag',
            tag: exclusiveTag1
          });
          continue;
        }
        
        // PRIORITY 3: Hardcoded conflicts
        const conflicts1 = this.productConflicts[item1.product_id];
        const conflicts2 = this.productConflicts[item2.product_id];
        
        if (conflicts1 && conflicts1.includes(item2.product_id)) {
          conflicts.push({
            product1: {
              id: item1.product_id,
              title: item1.product?.title || item1.title || 'Product'
            },
            product2: {
              id: item2.product_id,
              title: item2.product?.title || item2.title || 'Product'
            },
            type: 'hardcoded'
          });
        } else if (conflicts2 && conflicts2.includes(item1.product_id)) {
          conflicts.push({
            product1: {
              id: item1.product_id,
              title: item1.product?.title || item1.title || 'Product'
            },
            product2: {
              id: item2.product_id,
              title: item2.product?.title || item2.title || 'Product'
            },
            type: 'hardcoded'
          });
        }
      }
    }

    return conflicts;
  }

  /**
   * Get warning message for cart conflicts
   * @param {Array} conflicts - Array of conflict objects
   * @returns {string|null} Warning message or null
   */
  getCartWarningMessage(conflicts) {
    if (!conflicts || conflicts.length === 0) {
      return null;
    }

    const productNames = new Set();
    conflicts.forEach(conflict => {
      productNames.add(conflict.product1.title);
      productNames.add(conflict.product2.title);
    });

    const hasGalette = Array.from(productNames).some(name => 
      name.toLowerCase().includes('galette') || name.toLowerCase().includes('rois')
    );
    const hasPanettone = Array.from(productNames).some(name => 
      name.toLowerCase().includes('panettone')
    );

    if (hasGalette && hasPanettone) {
      const defaultMessage = `These products have different availability periods and cannot be ordered together.`;
      
      if (window.cartValidationStrings?.cartConflictWarningSpecific) {
        return window.cartValidationStrings.cartConflictWarningSpecific;
      }
      return defaultMessage;
    }

    const defaultMessage = `These products have different availability periods and cannot be ordered together. Please complete your order for one product first, then place a separate order for the other.`;
    
    if (window.cartValidationStrings?.cartConflictWarning) {
      return window.cartValidationStrings.cartConflictWarning
        .replace('[product_list]', Array.from(productNames).join(' and '));
    }
    
    return defaultMessage;
  }
}

// Export singleton instance
window.cartValidation = new CartValidation();
